import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiService } from "../../../utils/api";
import html2pdf from "html2pdf.js";
import { FaDownload, FaArrowLeft } from "react-icons/fa";
import "../../ComparisonResults.css";
import "../TeacherComparisonResults.css";

function TeacherImageComparisonResults() {
  const { studentId, documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeTaken, setTimeTaken] = useState("0:00");
  const [accuracy, setAccuracy] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [student, setStudent] = useState(null);
  const [rollNo, setRollNo] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const resultsContainerRef = useRef(null);

  useEffect(() => {
    fetchStudentDocumentComparison();
  }, [studentId, documentId]);

  async function fetchStudentDocumentComparison() {
    try {
      setLoading(true);
      // Add this method to the API service
      const response = await apiService.getStudentImageDocumentComparison(studentId, documentId);
      const data = response.data;

      if (data.success) {
        setDocument(data.document);
        setStudent(data.student);

        if (data.comparisonResults) {
          setComparisonResults(data.comparisonResults);
          setAccuracy(data.comparisonResults.accuracy || data.comparisonResults.matchPercentage);
          setSpeed(data.comparisonResults.typingSpeed || 0);
          setTimeTaken(data.comparisonResults.timeTaken || "0:00");
          setRollNo(data.student.rollNo);
        } else {
          setError("No comparison results available for this document");
        }
      } else {
        setError(data.message || "Failed to load document");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching student document and comparison");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const renderFormattedText = () => {
    if (comparisonResults?.formattedText) {
      return <div dangerouslySetInnerHTML={{ __html: comparisonResults.formattedText }} />;
    } else if (comparisonResults?.wordByWordComparison) {
      // Render from word-by-word comparison if formattedText is not available
      return (
        <div className="formatted-text">
          {comparisonResults.wordByWordComparison.map((item, index) => {
            if (item.correct) {
              return (
                <span key={index} className="correct">
                  {item.text}{" "}
                </span>
              );
            } else if (item.incorrect) {
              return (
                <span key={index}>
                  <span className="incorrect">{item.text}</span> <span className="correction">{item.correction}</span>{" "}
                </span>
              );
            } else if (item.extra) {
              return (
                <span key={index} className="extra">
                  {item.text}{" "}
                </span>
              );
            }
            return null;
          })}
        </div>
      );
    } else {
      return <div className="formatted-text">No comparison data available</div>;
    }
  };

  const generatePDF = async () => {
    if (!resultsContainerRef.current) return;

    setGeneratingPdf(true);
    const element = resultsContainerRef.current;
    const opt = {
      margin: 10,
      filename: `${student?.username || "student"}_document_results.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const goBack = () => {
    navigate(`/dashboard/teacher/students/${studentId}`);
  };

  if (loading) {
    return (
      <div className="comparison-results-container">
        <div className="loading">Loading results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="comparison-results-container">
        <div className="error-message">{error}</div>
        <button onClick={goBack} className="back-button">
          <FaArrowLeft /> Back to Student Documents
        </button>
      </div>
    );
  }

  return (
    <div className={`comparison-results-container ${generatingPdf ? "generating-pdf" : ""}`} ref={resultsContainerRef}>
      <div className="results-header">
        <button onClick={goBack} className="back-button" style={{ display: generatingPdf ? "none" : "flex" }}>
          <FaArrowLeft /> Back to Student Documents
        </button>
        <button onClick={generatePDF} className="download-button" style={{ display: generatingPdf ? "none" : "flex" }}>
          <FaDownload /> Download PDF
        </button>
      </div>

      
      <h2>NEW PITMAN STENO TYPING INSTITUTE PRAYAGRAJ</h2>

      <div className="student-info-container">
        
        <div className="student-details">
          <h3>Student Information</h3>
          <p>
            <strong>Name:</strong> {student?.username || "N/A"}
          </p>
          <p>
            <strong>Roll Number:</strong> {rollNo || "N/A"}
          </p>
          <p>
            <strong>Time Taken:</strong> {timeTaken}
          </p>
          <p>
            <strong>Accuracy:</strong> {accuracy.toFixed(2)}%
          </p>
        </div>

        <div className="document-details">
          <h3>Document Information</h3>
          <p>
            <strong>Title:</strong> {document?.title || "Untitled"}
          </p>
          <p>
            <strong>Created:</strong> {document?.createdAt ? new Date(document.createdAt).toLocaleDateString() : "N/A"}
          </p>
          <p>
            <strong>Text Length:</strong> {document?.content?.length || 0} characters
          </p>
          <p>
            <strong>Typing Speed:</strong> {speed.toFixed(2)} WPM
          </p>
        </div>
      </div>

      <div className="comparison-content">
        <h3>Document Comparison</h3>
        <div className="formatted-content">{renderFormattedText()}</div>
      </div>

      <div className="stats-container">
        <div className="stat-box match-score">
          <h3>Total Missing</h3>
          <div className={`score-value ${accuracy >= 90 ? "high" : accuracy >= 70 ? "medium" : "low"}`}>{comparisonResults.missingWords.length}</div>
        </div>

        <div className="stat-box typing-speed">
          <h3>Typing Speed</h3>
          <div className="score-value">{comparisonResults.wordByWordComparison.filter((item) => item.correct === false).length}</div>
        </div>

        <div className="stat-box typing-speed">
          <h3>Dot Error</h3>
          <div className="score-value">{comparisonResults.wordByWordComparison.filter((item) => (item.original === "." || item.original?.endsWith(".")) && item.correct === false).length * 3}</div>
        </div>
      </div>
    </div>
  );
}

export default TeacherImageComparisonResults;
